/**
 * Routing Notes Service
 *
 * Multi-tenant aware service for CRUD operations on routing notes.
 * Notes are stored in Firestore at: tenants/{tenantId}/routing_notes
 *
 * Each note allows users to override pickup dates, track reasons,
 * and document issues for chargeback defense.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { FirebaseService } from '../../../shared/services/FirebaseService';

class RoutingNotesServiceClass {
  constructor() {
    this.db = null;
    this.tenantId = null;
    this.initialized = false;
    console.log('🔧 RoutingNotesService initialized');
  }

  /**
   * Set the current tenant context
   * @param {string} tenantId - The tenant ID (e.g., 'inmocean', 'acme')
   */
  setTenant(tenantId) {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    this.tenantId = tenantId;
    console.log(`🏢 RoutingNotesService tenant set to: ${tenantId}`);
  }

  /**
   * Get current tenant ID
   */
  getTenantId() {
    if (!this.tenantId) {
      throw new Error('Tenant not set. Call setTenant(tenantId) first.');
    }
    return this.tenantId;
  }

  /**
   * Initialize Firestore connection
   */
  async initialize() {
    if (this.initialized && this.db) return;

    try {
      await FirebaseService.initialize();
      this.db = FirebaseService.getFirestore();
      this.initialized = true;
      console.log('✅ RoutingNotesService Firestore initialized');
    } catch (error) {
      console.error('❌ Failed to initialize RoutingNotesService:', error);
      throw error;
    }
  }

  /**
   * Get the collection reference for current tenant's routing notes
   */
  getNotesCollection() {
    const tenantId = this.getTenantId();
    return collection(this.db, `tenants/${tenantId}/routing_notes`);
  }

  /**
   * Generate a unique note ID
   */
  generateNoteId() {
    return `note_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }

  /**
   * Create a new routing note
   * @param {Object} noteData - The note data
   * @returns {Object} The created note with ID
   */
  async createNote(noteData) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`📝 Creating routing note for tenant ${tenantId}:`, noteData.po_number);

      const noteId = this.generateNoteId();
      const currentUser = FirebaseService.getCurrentUser();

      const note = {
        id: noteId,
        po_number: noteData.po_number || '',
        load_id: noteData.load_id || '',

        // Override Data
        original_pickup_date: noteData.original_pickup_date || '',
        override_pickup_date: noteData.override_pickup_date || '',

        // Tracking
        reason: noteData.reason || '',
        responsibility: noteData.responsibility || '',
        note_text: noteData.note_text || '',
        ticket_number: noteData.ticket_number || '',
        ticket_sent: noteData.ticket_sent || false,

        // Context data (for display in Notes table)
        mabd: noteData.mabd || '',
        warehouse: noteData.warehouse || '',
        destination: noteData.destination || '',
        cases: noteData.cases || 0,

        // Metadata
        created_by: currentUser?.email || 'unknown',
        created_at: serverTimestamp(),
        updated_by: currentUser?.email || 'unknown',
        updated_at: serverTimestamp()
      };

      const docRef = doc(this.db, `tenants/${tenantId}/routing_notes`, noteId);
      await setDoc(docRef, note);

      console.log(`✅ Routing note created: ${noteId}`);
      return { ...note, id: noteId };

    } catch (error) {
      console.error('❌ Failed to create routing note:', error);
      throw new Error(`Failed to create note: ${error.message}`);
    }
  }

  /**
   * Update an existing routing note
   * @param {string} noteId - The note ID to update
   * @param {Object} updates - The fields to update
   * @returns {Object} The updated note
   */
  async updateNote(noteId, updates) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`📝 Updating routing note ${noteId} for tenant ${tenantId}`);

      const currentUser = FirebaseService.getCurrentUser();
      const docRef = doc(this.db, `tenants/${tenantId}/routing_notes`, noteId);

      // Remove fields that shouldn't be updated
      const safeUpdates = { ...updates };
      delete safeUpdates.id;
      delete safeUpdates.created_by;
      delete safeUpdates.created_at;

      await updateDoc(docRef, {
        ...safeUpdates,
        updated_by: currentUser?.email || 'unknown',
        updated_at: serverTimestamp()
      });

      console.log(`✅ Routing note updated: ${noteId}`);
      return { id: noteId, ...safeUpdates };

    } catch (error) {
      console.error('❌ Failed to update routing note:', error);
      throw new Error(`Failed to update note: ${error.message}`);
    }
  }

  /**
   * Delete a routing note
   * @param {string} noteId - The note ID to delete
   */
  async deleteNote(noteId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`🗑️ Deleting routing note ${noteId} for tenant ${tenantId}`);

      const docRef = doc(this.db, `tenants/${tenantId}/routing_notes`, noteId);
      await deleteDoc(docRef);

      console.log(`✅ Routing note deleted: ${noteId}`);
      return { id: noteId, deleted: true };

    } catch (error) {
      console.error('❌ Failed to delete routing note:', error);
      throw new Error(`Failed to delete note: ${error.message}`);
    }
  }

  /**
   * Get a note by its ID
   * @param {string} noteId - The note ID
   * @returns {Object|null} The note or null if not found
   */
  async getNoteById(noteId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const docRef = doc(this.db, `tenants/${tenantId}/routing_notes`, noteId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() };

    } catch (error) {
      console.error('❌ Failed to get routing note:', error);
      throw error;
    }
  }

  /**
   * Get note(s) by PO number
   * @param {string} poNumber - The PO number to search
   * @returns {Array} Array of notes for this PO
   */
  async getNotesByPO(poNumber) {
    await this.initialize();

    try {
      const notesRef = this.getNotesCollection();
      const q = query(notesRef, where('po_number', '==', poNumber));
      const querySnapshot = await getDocs(q);

      const notes = [];
      querySnapshot.forEach((doc) => {
        notes.push({ id: doc.id, ...doc.data() });
      });

      return notes;

    } catch (error) {
      console.error('❌ Failed to get notes by PO:', error);
      throw error;
    }
  }

  /**
   * Get note(s) by Load ID
   * @param {string} loadId - The Load ID to search
   * @returns {Array} Array of notes for this Load
   */
  async getNotesByLoadId(loadId) {
    await this.initialize();

    try {
      const notesRef = this.getNotesCollection();
      const q = query(notesRef, where('load_id', '==', loadId));
      const querySnapshot = await getDocs(q);

      const notes = [];
      querySnapshot.forEach((doc) => {
        notes.push({ id: doc.id, ...doc.data() });
      });

      return notes;

    } catch (error) {
      console.error('❌ Failed to get notes by Load ID:', error);
      throw error;
    }
  }

  /**
   * Get all notes for the current tenant
   * @returns {Array} Array of all notes
   */
  async getAllNotes() {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`📋 Fetching all routing notes for tenant ${tenantId}`);

      const notesRef = this.getNotesCollection();
      const q = query(notesRef, orderBy('created_at', 'desc'));
      const querySnapshot = await getDocs(q);

      const notes = [];
      querySnapshot.forEach((doc) => {
        notes.push({ id: doc.id, ...doc.data() });
      });

      console.log(`✅ Retrieved ${notes.length} routing notes`);
      return notes;

    } catch (error) {
      console.error('❌ Failed to get all notes:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for all notes
   * @param {Function} callback - Callback function receiving notes array
   * @returns {Function} Unsubscribe function
   */
  subscribeToNotes(callback) {
    if (!this.db) {
      console.error('❌ Firestore not initialized. Call initialize() first.');
      return () => {};
    }

    const tenantId = this.getTenantId();
    console.log(`🔔 Subscribing to routing notes for tenant ${tenantId}`);

    const notesRef = this.getNotesCollection();
    const q = query(notesRef, orderBy('created_at', 'desc'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const notes = [];
      querySnapshot.forEach((doc) => {
        notes.push({ id: doc.id, ...doc.data() });
      });

      console.log(`🔄 Routing notes updated: ${notes.length} notes`);
      callback(notes);
    }, (error) => {
      console.error('❌ Error listening to routing notes:', error);
    });

    return unsubscribe;
  }

  /**
   * Get notes as a lookup map keyed by PO number
   * @returns {Object} Map of po_number -> note
   */
  async getNotesLookup() {
    const notes = await this.getAllNotes();
    const lookup = {};

    notes.forEach(note => {
      if (note.po_number) {
        // If multiple notes for same PO, keep the most recent (first in array since sorted desc)
        if (!lookup[note.po_number]) {
          lookup[note.po_number] = note;
        }
      }
    });

    return lookup;
  }
}

// Export singleton instance
export const RoutingNotesService = new RoutingNotesServiceClass();

// Also export class for testing
export { RoutingNotesServiceClass };
